// The activity (task) split of a weekly timesheet — pure arithmetic, no DOM,
// no network. The one hard requirement, and the reason this module exists:
// however a day is split, the pieces MUST re-add to exactly the hours the
// foreman typed. Money is priced from these rows downstream (loaded rate ×
// hours), so a rounding leak here becomes a payroll/job-cost discrepancy.
// Largest-remainder at 2 decimals guarantees the sum; nothing here may
// invent, drop, or scale an hour.

// A paid sick day pays exactly one standard day of regular hours. Eight is
// only the FALLBACK — the length of a standard day is the org's
// (organizations.paid_sick_hours, editable in Settings), because an eight-hour
// day is a convention, not a law, and a company on 7.5 or 10 would otherwise be
// silently held to ours.
export const PAID_SICK_HOURS = 8;

// What THIS cell's sick day is worth, in hours.
//
// The number is stamped onto the cell at submit time (app.js
// _annotateUnpaidSickDays), not looked up when the row is read. That is the
// whole point: a timesheet signed under a 7.5-hour rule must keep paying 7.5
// after the company moves to 8, and its PDF must still regenerate with the
// number it was signed with. A cell from before this existed carries nothing
// and falls back to eight — exactly what it was written with.
//
// The DB write (app.js syncTimesheetToDb), the PDF day-grid
// (pdf-generator.js) and the per-employee task summary below all read this one
// function, so the three surfaces can never disagree on what a sick day is
// worth.
export function sickHoursOf(cell) {
  if (!cell || !cell.sick) return 0;
  if (cell.sick_unpaid) return 0;          // over the yearly allowance
  const n = Number(cell.sick_hours);
  return Number.isFinite(n) && n > 0 ? n : PAID_SICK_HOURS;
}

// The org's current rule, for a filing being written NOW and for the labels
// that quote the policy. Number('') and Number(null) are both 0, so a plain
// isFinite check would read "never set" as "this org pays nothing" — only a
// real positive number counts.
export function orgPaidSickHours(org) {
  const n = Number(org && org.paidSickHours);
  return Number.isFinite(n) && n > 0 ? n : PAID_SICK_HOURS;
}

const r2 = v => Math.round(v * 100) / 100;

function largestRemainder(total, weights) {
  // Distribute `total` (2-dp) across weights (pcts), exact sum by construction.
  if (!weights.length || total <= 0) return weights.map(() => 0);
  const raw = weights.map(w => total * w / 100);
  const floored = raw.map(v => Math.floor(v * 100) / 100);
  let rest = Math.round((total - floored.reduce((s, v) => s + v, 0)) * 100);
  const order = raw.map((v, i) => [v - floored[i], i]).sort((a, b) => b[0] - a[0]);
  const out = floored.slice();
  for (let k = 0; rest > 0; k = (k + 1) % order.length, rest -= 1) {
    out[order[k][1]] = r2(out[order[k][1]] + 0.01);
  }
  return out;
}

export function splitDayHours(reg, ot, split) {
  const entries = (split || [])
    .map(s => ({ task_id: s.task_id || null, pct: Number(s.pct) || 0 }))
    .filter(s => s.pct > 0);
  if (!entries.length) return [];
  const regParts = largestRemainder(r2(Number(reg) || 0), entries.map(e => e.pct));
  const otParts  = largestRemainder(r2(Number(ot)  || 0), entries.map(e => e.pct));
  return entries.map((e, i) => ({
    task_id: e.task_id === '' ? null : e.task_id,
    regular_hours: regParts[i],
    overtime_hours: otParts[i],
    hours_worked: r2(regParts[i] + otParts[i]),
  })).filter(r => r.hours_worked > 0);
}

// ── Paid sick hours ride on the job's tasks ─────────────────────────────────
// 2026-09-25 (Renan): "every time the sick days have to be included in the job
// of the timesheet — spread across all the items, in accordance of the job".
// The DB row for a paid sick day is task-less on purpose (app.js
// syncTimesheetToDb): nobody worked that day, so "no task" is the FACT. The
// cost view derives the spread when it READS:
//   1. a sick day's hours go to the tasks of its OWN timesheet, in proportion
//      to the hours the crew put on each that week;
//   2. a sheet with no tasked hours borrows the job's mix for the SAME WEEK
//      (Mon–Sun of the sick day, same project) — a closed week never changes,
//      so a past sick day's split never drifts as the job accrues hours;
//   3. a week with no tasked hours at all keeps the row where it was
//      (General) — every pre-task sheet stays exactly as it was.
// Largest-remainder at 2 dp, so the pieces re-add to the sick hours exactly:
// the trade's total never moves, only its by-task split.
//
// Which rows are sick is decided by the sick_days ledger, never by
// "task-less" (a sheet whose foreman named no task holds untasked WORK, and
// work is not spread). A ledger row written by the sync carries the
// timesheet's submission_id and matches only that sheet's rows, so a stale
// ledger row from a week filed twice can never turn a worked day into sick
// pay; rows from before that column existed (2026-08-16) carry the sync's
// literal "From timesheet — " note instead and match by worker + day, and
// only a paid row that itself predates the column (no submission_id) — so
// a re-filed week can never inherit one. An UNPAID day (over the allowance)
// has no paid row and keys nothing. A day typed by hand on the Sick Days tab
// (no submission, no such note) has no paid row to spread — and if the
// person also worked part of that day, that row is work.
export const sickKey = (workerId, date, submissionId) =>
  `${workerId || ''}|${String(date || '').slice(0, 10)}|${submissionId || ''}`;

// The sync's exact prefix (app.js syncTimesheetToDb) — case and em dash
// included, so a hand-typed "from timesheet, half day" never qualifies.
const FROM_TIMESHEET = /^From timesheet — /;

export function sickKeysOf(sickDays) {
  const keys = new Set();
  (sickDays || []).forEach(s => {
    if (!s || !s.worker_id || !s.sick_date) return;
    const note = String(s.note || '');
    if (/UNPAID/.test(note)) return;                       // no paid row exists for it
    if (s.submission_id) keys.add(sickKey(s.worker_id, s.sick_date, s.submission_id));
    else if (FROM_TIMESHEET.test(note)) keys.add(sickKey(s.worker_id, s.sick_date, ''));
  });
  return keys;
}

// Monday of the week holding an ISO date — the window rule 2 above uses.
export function weekMondayOf(date) {
  const d = String(date || '').slice(0, 10);
  const t = Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10));
  if (!Number.isFinite(t)) return '';
  const dow = (new Date(t).getUTCDay() + 6) % 7;            // Mon = 0
  return new Date(t - dow * 86400000).toISOString().slice(0, 10);
}

export function spreadSickAcrossTasks(entries, sickKeys) {
  const list = entries || [];
  if (!sickKeys || !sickKeys.size) return list;
  const hoursOf = e => (Number(e.regular_hours) || 0) + (Number(e.overtime_hours) || 0);
  const isSick = e => !e.task_id && hoursOf(e) > 0 &&
    (e.submission_id ? sickKeys.has(sickKey(e.worker_id, e.work_date, e.submission_id))
                     : sickKeys.has(sickKey(e.worker_id, e.work_date, '')));
  const weekKey = e => `${e.project_id || ''}|${weekMondayOf(e.work_date)}`;
  // The task mix per timesheet, and per (job, week) as the fallback — tasked
  // WORK only, never a sick row and never another sick day's pieces.
  const bySheet = {}, byWeek = {};
  const add = (map, key, e) => {
    const m = map[key] || (map[key] = {});
    m[e.task_id] = (m[e.task_id] || 0) + hoursOf(e);
  };
  list.forEach(e => {
    if (!e.task_id) return;
    if (e.submission_id) add(bySheet, e.submission_id, e);
    add(byWeek, weekKey(e), e);
  });
  const toSplit = (mix) => {
    if (!mix) return null;
    const total = Object.values(mix).reduce((s, h) => s + h, 0);
    return total > 0 ? Object.entries(mix).map(([task_id, h]) => ({ task_id, pct: h / total * 100 })) : null;
  };
  const out = [];
  list.forEach(e => {
    if (!isSick(e)) { out.push(e); return; }
    const split = toSplit((e.submission_id && bySheet[e.submission_id]) || byWeek[weekKey(e)]);
    if (!split) { out.push(e); return; }
    splitDayHours(e.regular_hours, e.overtime_hours, split).forEach(p => out.push({
      ...e, task_id: p.task_id, regular_hours: p.regular_hours, overtime_hours: p.overtime_hours,
      hours_worked: p.hours_worked, sick_spread: true,
    }));
  });
  return out;
}

const one = (taskId, reg, ot) => [{
  task_id: taskId === '' || taskId == null ? null : taskId,
  regular_hours: r2(reg), overtime_hours: r2(ot), hours_worked: r2(reg + ot),
}];

export function resolveWorkerDay(cell, dayCfg, weekTask, workerIdx) {
  const reg = parseFloat(cell && cell.r) || 0;
  const ot  = parseFloat(cell && cell.ot) || 0;
  if (reg + ot <= 0) return null;
  if (!dayCfg) return one(weekTask, reg, ot);
  if ('task_id' in dayCfg && !dayCfg.split) return one(dayCfg.task_id, reg, ot);
  const override = dayCfg.workers && dayCfg.workers[workerIdx];
  if (override && override.length) {
    // Overrides are absolute hours for the whole day; distribute reg+ot by
    // the override hours as weights to guarantee exact re-add (largest-remainder).
    const totalH = (override || []).reduce((s, o) => s + (Number(o.hours) || 0), 0);
    if (totalH > 0) {
      const pcts = (override || []).map(o => (Number(o.hours) || 0) / totalH * 100);
      const regParts = largestRemainder(r2(reg), pcts);
      const otParts  = largestRemainder(r2(ot), pcts);
      return (override || []).map((o, i) => ({
        task_id: o.task_id === '' || o.task_id == null ? null : o.task_id,
        regular_hours: regParts[i],
        overtime_hours: otParts[i],
        hours_worked: r2(regParts[i] + otParts[i]),
      })).filter(r => r.hours_worked > 0);
    }
  }
  return splitDayHours(reg, ot, dayCfg.split);
}

export function validateActivity(employees, activity) {
  const errs = [];
  if (!activity || !activity.days) return errs;
  Object.entries(activity.days).forEach(([day, cfg]) => {
    if (!cfg || !cfg.split) return;
    const pct = (cfg.split || []).reduce((s, x) => s + (Number(x.pct) || 0), 0);
    if (Math.abs(pct - 100) > 0.01) {
      errs.push(`The ${day} split adds to ${pct}% — it must be 100%.`);
    }
    Object.entries(cfg.workers || {}).forEach(([idx, rows]) => {
      const emp = (employees || [])[idx];
      const cell = emp && emp.days && emp.days[day];
      const total = (parseFloat(cell && cell.r) || 0) + (parseFloat(cell && cell.ot) || 0);
      const sum = (rows || []).reduce((s, x) => s + (Number(x.hours) || 0), 0);
      if (Math.abs(sum - total) > 0.01) {
        errs.push(`${(emp && emp.name) || 'A worker'}'s ${day} split adds to ${sum}h but the day has ${total}h.`);
      }
    });
  });
  return errs;
}

const TS_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Per-worker task buckets for one week — the same per-day resolution
// summarizeActivity always used, just kept separate per employee instead of
// pooled. summarizeActivity below is built ON TOP of this (column-sums it),
// so the crew total and the per-employee rows can never drift apart: there
// is only one place that walks days/resolves overrides.
//
// sickHours is tracked alongside byTask, never inside it: a sick day is not
// task work, so it must never be attributable to a task_id (or to General).
// It applies the same paid-sick rule as the day-grid (PAID_SICK_HOURS of REG
// when cell.sick and not cell.sick_unpaid; 0h when over the yearly
// allowance) so a caller that adds byTask + sickHours reproduces the grid's
// own week total exactly.
export function summarizeActivityByEmployee(employees, activity) {
  return (employees || []).map((emp, wIdx) => {
    const byTask = {};
    let sickHours = 0;
    TS_DAYS.forEach(day => {
      const cell = emp && emp.days && emp.days[day];
      if (!cell) return;
      if (cell.sick) {
        sickHours = r2(sickHours + sickHoursOf(cell));
        return;
      }
      const rows = resolveWorkerDay(cell, activity && activity.days && activity.days[day],
        (activity && activity.week) || '', wIdx);
      (rows || []).forEach(rw => {
        const k = rw.task_id || '';
        byTask[k] = r2((byTask[k] || 0) + rw.hours_worked);
      });
    });
    return { name: (emp && emp.name) || '', byTask, sickHours };
  });
}

export function summarizeActivity(employees, activity) {
  const sum = {};
  summarizeActivityByEmployee(employees, activity).forEach(({ byTask }) => {
    Object.entries(byTask).forEach(([k, h]) => { sum[k] = r2((sum[k] || 0) + h); });
  });
  return sum;
}

// ── The editor's helpers and the sheet → rows walk (2026-10-19) ─────────────
// Moved here from js/app.js so the admin's "Re-file hours" and the foreman's
// Activity bar run the SAME code: one dialog (timesheet-activity-ui.js), one
// walk from a signed sheet to time_entries rows (sheetRows). Nothing below
// reads the field app's state, the DOM, storage or the network.

// Seed rows for the Split dialog: the day's current split, else its current
// single-task override, else the week's default task at 100%.
export function splitSeedRows(dayCfg, weekTask) {
  if (dayCfg && Array.isArray(dayCfg.split) && dayCfg.split.length) {
    return dayCfg.split.map(s => ({ task_id: s.task_id || '', pct: Number(s.pct) || 0 }));
  }
  if (dayCfg && 'task_id' in dayCfg) {
    return [{ task_id: dayCfg.task_id || '', pct: 100 }];
  }
  return [{ task_id: weekTask || '', pct: 100 }];
}

export function proportionalHours(dayTotal, rows, taskId) {
  const totalPct = rows.reduce((s, r) => s + (Number(r.pct) || 0), 0);
  if (totalPct <= 0) return 0;
  const row = rows.find(r => (r.task_id || '') === taskId);
  if (!row) return 0;
  return Math.round(dayTotal * (Number(row.pct) || 0) / totalPct * 100) / 100;
}

// The top-level `split` entry a By-worker save writes alongside `workers` is
// a fallback ONLY — resolveWorkerDay reaches it for a crew member who wasn't
// on the timesheet yet when this day was split (added after Save). Each
// row's dominant activity (its largest-hours entry — for a single-chip row
// that IS the chip) casts one vote; the most-voted activity wins, and the
// week's default activity breaks a tie or covers the everyone-zeroed case.
// `rowEntries` is `{ [rowIdx]: { [task_id]: hours } }`.
export function byWorkerFallbackTask(rowEntries, weekTask) {
  const counts = {};
  Object.values(rowEntries).forEach(entries => {
    let bestKey = '', bestHours = 0;
    Object.entries(entries).forEach(([k, h]) => {
      const hv = Number(h) || 0;
      if (hv > bestHours) { bestHours = hv; bestKey = k; }
    });
    if (bestHours > 0) counts[bestKey] = (counts[bestKey] || 0) + 1;
  });
  let best = weekTask || '', bestCount = 0;
  Object.entries(counts).forEach(([k, c]) => { if (c > bestCount) { best = k; bestCount = c; } });
  return best;
}

// A task can be renamed or deleted in Settings after a draft was written
// against it. Never let a stale id reach the sync: it's a real FK
// (time_entries.task_id → project_contract_lines, ON DELETE SET NULL) and
// the week is written as one transaction — one bad id would abort every row,
// not just its own. Mutates `activity` in place.
//
// An unknown WEEK task becomes null — "not chosen" — never '' (General):
// since 2026-10-19 the field asks again when the job has two or more tasks,
// and the admin's Save waits for a pick; silently booking a deleted task's
// week to General was the leak this whole feature exists to close.
//
// `empCount` is the crew size: a per-worker override keyed to an employee
// position that no longer exists (an old draft saved before the reindex fix,
// or any other corruption) must never reach the sync — it would apply the
// wrong worker's hours to whoever now sits at that index.
export function sanitizeActivityIds(activity, tasks, empCount) {
  const known = new Set((tasks || []).map(t => t.task_id));
  const fix = id => (id && known.has(id)) ? id : null;
  if (!activity) return;
  if (activity.week && !known.has(activity.week)) activity.week = null;
  if (!activity.days) return;
  const n = Number(empCount) || 0;
  Object.keys(activity.days).forEach(day => {
    const cfg = activity.days[day];
    if (!cfg) { delete activity.days[day]; return; }
    if (Array.isArray(cfg.split)) {
      cfg.split.forEach(s => { s.task_id = fix(s.task_id); });
      if (cfg.workers) {
        Object.keys(cfg.workers).forEach(idx => {
          if (Number(idx) >= n) { delete cfg.workers[idx]; return; }
          (cfg.workers[idx] || []).forEach(w => { w.task_id = fix(w.task_id); });
        });
        if (!Object.keys(cfg.workers).length) delete cfg.workers;
      }
    } else if ('task_id' in cfg) {
      cfg.task_id = fix(cfg.task_id);
    }
  });
}

// ISO date + n days, in UTC like weekMondayOf — calendar arithmetic only, so
// a DST Sunday never shifts a work_date.
export function addDays(iso, n) {
  const d = String(iso || '').slice(0, 10);
  const t = Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10)) + (Number(n) || 0) * 86400000;
  return Number.isFinite(t) ? new Date(t).toISOString().slice(0, 10) : '';
}

// The signed sheet, resolved: one time_entries row per (worker, day, task).
// This is the loop syncTimesheetToDb always ran; the admin re-file now runs
// the same one, so the office can never derive a different week than the
// phone did.
//   rows    — [{ worker_id, work_date, regular_hours, overtime_hours,
//                hours_worked, task_id|null }], zero-hour rows already dropped;
//                a clock day's rows add start_time, end_time, break_minutes
//   sick    — [{ worker_id, name, work_date, cell }] every sick cell, paid or
//             not (the sick_days ledger wants both; only a paid one has a row)
//   skipped — [{ name, hours }] crew rows with hours or a sick day but no
//             worker on file. A blank row with nothing on it is simply
//             ignored, as the field ignores it.
// `workerIdFor(emp, idx)` answers the worker id for a crew row ('' = none).
export function sheetRows(employees, activity, weekOfIso, workerIdFor) {
  const rows = [], sick = [], skipped = [];
  const days = (activity && activity.days) || {};
  const week = (activity && activity.week) || '';
  (employees || []).forEach((emp, idx) => {
    const workerId = workerIdFor ? workerIdFor(emp, idx) : ((emp && emp.worker_id) || '');
    if (!workerId) {
      let hours = 0, anySick = false;
      TS_DAYS.forEach(d => {
        const c = (emp && emp.days && emp.days[d]) || {};
        if (c.sick) anySick = true;
        hours += (parseFloat(c.r) || 0) + (parseFloat(c.ot) || 0);
      });
      if (hours > 0 || anySick) skipped.push({ name: ((emp && emp.name) || '').trim(), hours: r2(hours) });
      return;
    }
    TS_DAYS.forEach((day, d) => {
      const cell = (emp && emp.days && emp.days[day]) || {};
      const work_date = addDays(weekOfIso, d);
      // A day the time clock filled carries its punches' times on every row it
      // yields (time_entries.start_time / end_time / break_minutes); a typed
      // cell carries no key at all.
      const times = cell.clock ? { start_time: cell.clock.start24 ?? null, end_time: cell.clock.end24 ?? null,
                                   break_minutes: cell.clock.brk ?? 0 } : {};
      if (cell.sick) {
        sick.push({ worker_id: workerId, name: ((emp && emp.name) || '').trim(), work_date, cell });
        // A PAID sick day is a full day of regular hours on no task — nobody
        // worked, so "no task" is the fact; the cost view spreads it when it
        // reads (spreadSickAcrossTasks). Over the allowance: no row at all.
        const h = sickHoursOf(cell);
        if (h > 0) rows.push({ worker_id: workerId, work_date, regular_hours: h, overtime_hours: 0, hours_worked: h, task_id: null, ...times });
        return;
      }
      (resolveWorkerDay(cell, days[day], week, idx) || []).forEach(rw => {
        if (!(rw.hours_worked > 0)) return;
        rows.push({ worker_id: workerId, work_date, regular_hours: rw.regular_hours,
                    overtime_hours: rw.overtime_hours, hours_worked: rw.hours_worked, task_id: rw.task_id, ...times });
      });
    });
  });
  return { rows, sick, skipped };
}
